import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveCliAuthorization,
  denyCliAuthorization,
  getCliAuthorization,
  listCliDevices,
  resolveActivationCode,
  revokeCliDevice,
  type CliApprovalResponse,
  type CliDenialResponse,
  type CliAuthorizationMetadata,
  type CliDeviceList,
} from "../api/cli.api";
import { ApiError } from "@/lib/api/client";

/** Query key factory for CLI authorizations and devices */
export const cliKeys = {
  all: ["cli"] as const,
  authorization: (id: string) => [...cliKeys.all, "authorization", id] as const,
  devices: () => [...cliKeys.all, "devices"] as const,
};

export function useCliAuthorization(authorizationId: string) {
  return useQuery<CliAuthorizationMetadata, ApiError>({
    queryKey: cliKeys.authorization(authorizationId),
    queryFn: () => getCliAuthorization(authorizationId),
    enabled: authorizationId.length > 0,
    // The request expires in minutes and a stale read would offer an approve
    // button for something the server has already retired.
    staleTime: 0,
    retry: false,
  });
}

export function useResolveActivationCode() {
  return useMutation<CliAuthorizationMetadata, ApiError, string>({
    mutationFn: resolveActivationCode,
  });
}

export function useApproveCliAuthorization() {
  const queryClient = useQueryClient();

  return useMutation<CliApprovalResponse, ApiError, string>({
    mutationFn: approveCliAuthorization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cliKeys.all });
    },
  });
}

export function useDenyCliAuthorization() {
  const queryClient = useQueryClient();

  return useMutation<CliDenialResponse, ApiError, string>({
    mutationFn: denyCliAuthorization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cliKeys.all });
    },
  });
}

export function useCliDevices() {
  return useQuery<CliDeviceList, ApiError>({
    queryKey: cliKeys.devices(),
    queryFn: listCliDevices,
  });
}

export function useRevokeCliDevice() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: revokeCliDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cliKeys.all });
    },
  });
}
